import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import './Favorites.css';

const TABS = [
  { key: 'STOCK', label: 'Stocks' },
  { key: 'MUTUAL_FUND', label: 'Mutual Funds' },
  { key: 'IPO', label: 'IPOs' }
];

function Favorites() {
  const [activeTab, setActiveTab] = useState('STOCK');
  const [favorites, setFavorites] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const searchRef = useRef(null);

  const closeSearch = useCallback(() => {
    setShowDropdown(false);
    setSearchResults([]);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') closeSearch();
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [closeSearch]);

  const fetchFavorites = useCallback(async () => {
    try {
      const response = await axios.get('/api/favorites', { params: { type: activeTab } });
      setFavorites(response.data);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error fetching favorites:', error);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    setLoading(true);
    fetchFavorites();
    const interval = setInterval(fetchFavorites, 15000);
    return () => clearInterval(interval);
  }, [fetchFavorites]);

  useEffect(() => {
    if (!showDropdown) return;

    const timer = setTimeout(async () => {
      try {
        const response = await axios.get('/api/market/search', {
          params: { type: activeTab, query: searchQuery || undefined }
        });
        setSearchResults(response.data);
      } catch (error) {
        console.error('Search error:', error);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, activeTab, showDropdown]);

  const addFavorite = async (symbol) => {
    try {
      await axios.post('/api/favorites', { symbol, assetType: activeTab });
      setSearchQuery('');
      closeSearch();
      fetchFavorites();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to add favorite');
    }
  };

  const removeFavorite = async (id) => {
    try {
      await axios.delete(`/api/favorites/${id}`);
      fetchFavorites();
    } catch (error) {
      console.error('Error removing favorite:', error);
    }
  };

  const formatPrice = (quote) => {
    if (!quote) return '—';
    if (quote.assetType === 'IPO') {
      if (quote.ipoStatus === 'LISTED' && quote.price != null) {
        return `₹${quote.price.toFixed(2)}`;
      }
      return `₹${quote.priceBandLow} – ₹${quote.priceBandHigh}`;
    }
    const sym = quote.currency === 'INR' ? '₹' : '$';
    const label = quote.assetType === 'MUTUAL_FUND' ? 'NAV' : '';
    return `${sym}${quote.price?.toFixed(2)}${label ? ' NAV' : ''}`;
  };

  const formatChange = (quote) => {
    if (!quote || quote.change == null) return null;
    const positive = quote.change >= 0;
    const sym = quote.currency === 'INR' ? '₹' : '$';
    return (
      <span className={positive ? 'change-up' : 'change-down'}>
        {positive ? '▲' : '▼'} {sym}{Math.abs(quote.change).toFixed(2)} ({quote.changePercent?.toFixed(2)}%)
      </span>
    );
  };

  const isFavorited = (symbol) => favorites.some(f => f.symbol === symbol);

  return (
    <div className="favorites-container">
      <div className="favorites-content">
        <div className="favorites-header">
          <h2>Market Favorites</h2>
          <p>Track stocks, mutual funds & IPOs with live dummy prices. +3 pts per favorite.</p>
          {lastRefresh && (
            <span className="refresh-hint">Prices refresh every 15s · Last: {lastRefresh.toLocaleTimeString()}</span>
          )}
        </div>

        <div className="tab-bar">
          {TABS.map(tab => (
            <button
              key={tab.key}
              className={activeTab === tab.key ? 'active' : ''}
              onClick={() => {
                setActiveTab(tab.key);
                setSearchQuery('');
                closeSearch();
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="search-section" ref={searchRef}>
          <input
            type="text"
            placeholder={`Search ${TABS.find(t => t.key === activeTab)?.label}...`}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setShowDropdown(true);
            }}
            onFocus={() => setShowDropdown(true)}
          />
          {showDropdown && searchResults.length > 0 && (
            <div className="search-dropdown">
              {searchResults.map(item => (
                <div key={item.symbol} className="search-item">
                  <div>
                    <strong>{item.symbol}</strong> — {item.name}
                    <div className="search-sub">{item.subtitle}</div>
                  </div>
                  {!isFavorited(item.symbol) ? (
                    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => addFavorite(item.symbol)}>+ Add</button>
                  ) : (
                    <span className="added-tag">Added</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {loading ? (
          <div className="loading">Loading favorites...</div>
        ) : favorites.length === 0 ? (
          <div className="favorites-empty">
            <h3>No {TABS.find(t => t.key === activeTab)?.label} in favorites</h3>
            <p>Search above to add {activeTab === 'IPO' ? 'an IPO' : 'one'}.</p>
          </div>
        ) : (
          <div className="favorites-grid">
            {favorites.map(fav => {
              const q = fav.liveQuote;
              return (
                <div key={fav.id} className="favorite-card">
                  <div className="favorite-top">
                    <div>
                      <div className="favorite-symbol">{fav.symbol}</div>
                      <div className="favorite-name">{fav.name}</div>
                    </div>
                    <button className="remove-btn" onClick={() => removeFavorite(fav.id)}>×</button>
                  </div>

                  {activeTab === 'IPO' ? (
                    <div className="ipo-details">
                      <div className="ipo-price-band">{formatPrice(q)}</div>
                      <div className={`ipo-status status-${q?.ipoStatus?.toLowerCase()}`}>{q?.ipoStatus}</div>
                      {q?.listingDate && <div className="ipo-date">Listing: {q.listingDate}</div>}
                      {q?.issueSize && <div className="ipo-size">Issue: {q.issueSize}</div>}
                      {q?.ipoStatus === 'LISTED' && formatChange(q)}
                    </div>
                  ) : (
                    <div className="price-section">
                      <div className="live-price">{formatPrice(q)}</div>
                      {formatChange(q)}
                    </div>
                  )}

                  <div className="live-badge">● LIVE (dummy)</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default Favorites;
